import { 
  useQuery, 
  useMutation, 
  useQueryClient 
} from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiUpdate, useApiDelete } from './use-api';
import { useCacheConfig } from '../contexts/cache-config-context';
import { queryKeys } from '../lib/query-key-factory';
import { 
  Product, 
  ProductListParams, 
  ProductListResponse, 
  ProductFormData 
} from '../types/product-types';
import { toast } from './use-toast';
import { useCacheControl } from './use-cache-control';

/**
 * Hook per ottenere un elenco paginato di prodotti con filtri
 */
export function useProducts(params: ProductListParams = {}) {
  // Ottiene la configurazione della cache per i prodotti
  const { config } = useCacheConfig();
  
  // Costruisce l'URL con i parametri di query
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.category) queryParams.append('category', params.category);
  if (params.search) queryParams.append('search', params.search);
  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy);
    queryParams.append('sortDirection', params.sortDirection || 'asc');
  }
  
  // Utilizza la query key definita nel factory
  return useApiQuery<ProductListResponse>(
    Array.from(queryKeys.products.list(params as Record<string, unknown>) as unknown as string[]),
    `/products?${queryParams.toString()}`,
    {
      queryKey: Array.from(queryKeys.products.list(params as Record<string, unknown>) as unknown as string[]),
      staleTime: config.products || 5 * 60 * 1000,
      gcTime: config.products || 10 * 60 * 1000,
    }
  );
}

/**
 * Hook per ottenere un singolo prodotto per ID
 */
export function useProduct(id: number | string) {
  const { config } = useCacheConfig();
  
  return useApiQuery<Product>(
    Array.from(queryKeys.products.detail(id) as unknown as string[]),
    `/products/${id}`,
    {
      queryKey: Array.from(queryKeys.products.detail(id) as unknown as string[]),
      staleTime: config.products || 5 * 60 * 1000,
      gcTime: config.products || 10 * 60 * 1000,
      enabled: !!id,
    }
  );
}

/**
 * Hook per creare un nuovo prodotto
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();
  
  return useApiMutation<Product, ProductFormData>(
    '/products',
    {
      onSuccess: () => {
        // Invalida la lista prodotti dopo la creazione di un nuovo prodotto
        queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
        
        toast({
          title: 'Prodotto creato',
          description: 'Il prodotto è stato creato con successo',
          status: 'success',
        });
      }
    }
  );
}

/**
 * Hook per aggiornare un prodotto esistente
 */
export function useUpdateProduct(id: number | string) {
  const queryClient = useQueryClient();
  
  return useApiUpdate<Product, ProductFormData>(
    `/products/${id}`,
    {
      onSuccess: (updatedProduct) => {
        // Aggiorna sia la query del dettaglio che la lista prodotti
        queryClient.setQueryData(
          queryKeys.products.detail(id), 
          updatedProduct
        );
        
        // Invalida solo la lista prodotti per rifletterci l'aggiornamento
        queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
        
        toast({
          title: 'Prodotto aggiornato',
          description: 'Il prodotto è stato aggiornato con successo',
          status: 'success',
        });
      }
    }
  );
}

/**
 * Hook per eliminare un prodotto
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  
  return useApiDelete<void, number | string>(
    '/products',
    {
      onSuccess: (_, deletedId) => {
        // Invalida sia il dettaglio del prodotto che la lista dei prodotti
        queryClient.removeQueries({ queryKey: queryKeys.products.detail(deletedId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
        
        toast({
          title: 'Prodotto eliminato',
          description: 'Il prodotto è stato eliminato con successo',
          status: 'success',
        });
      }
    }
  );
}

/**
 * Hook per ricaricare manualmente i dati dei prodotti
 */
export function useRefreshProducts() {
  const { invalidateEntityCache } = useCacheControl();
  
  return {
    refreshAllProducts: () => invalidateEntityCache('products'),
  };
}